import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Video,
  BookOpen,
  Folder,
  TrendingUp,
  Plus,
  Play,
  Clock,
  Users,
  ChevronRight
} from 'lucide-react';
import { useOnboarding } from '../components/OnboardingDialog';

const Dashboard = () => {
  const { user, api } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [mediaCount, setMediaCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showOnboarding, setShowOnboarding, OnboardingDialog } = useOnboarding();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [trainingsRes, mediaRes, analyticsRes] = await Promise.all([
        api.get('/trainings'),
        api.get('/media'),
        api.get('/analytics/user')
      ]);
      setTrainings(trainingsRes.data);
      setMediaCount(mediaRes.data.length);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: 'Formations',
      value: trainings.length,
      icon: BookOpen,
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      title: 'Vidéos',
      value: mediaCount,
      icon: Folder,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      title: 'Leçons complétées',
      value: analytics?.lessons_completed || 0,
      icon: TrendingUp,
      color: 'text-amber-600',
      bg: 'bg-amber-50'
    },
    {
      title: 'Temps de visionnage',
      value: `${Math.round((analytics?.total_watch_time || 0) / 60)}min`,
      icon: Clock,
      color: 'text-purple-600',
      bg: 'bg-purple-50'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 font-['Outfit']">
          Bonjour, {user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-500 mt-1">
          Voici un aperçu de votre activité
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, i) => (
          <Card key={i} className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 text-white">
            <Video className="w-10 h-10 mb-4 opacity-90" />
            <h3 className="text-xl font-semibold font-['Outfit'] mb-2">Studio de production</h3>
            <p className="text-blue-100 mb-4">
              Enregistrez votre écran et votre caméra pour créer des formations professionnelles.
            </p>
            <Link to="/studio">
              <Button 
                data-testid="go-to-studio-btn"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                <Play className="w-4 h-4 mr-2" />
                Ouvrir le studio
              </Button>
            </Link>
          </div>
        </Card>

        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-white">
            <BookOpen className="w-10 h-10 mb-4 opacity-90" />
            <h3 className="text-xl font-semibold font-['Outfit'] mb-2">Créer une formation</h3>
            <p className="text-slate-300 mb-4">
              Organisez vos vidéos en modules et leçons pour former vos équipes.
            </p>
            <Link to="/trainings/new">
              <Button 
                data-testid="create-training-btn"
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle formation
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* Recent Trainings */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Formations récentes
              </CardTitle>
              <CardDescription>
                Vos dernières formations créées
              </CardDescription>
            </div>
            <Link to="/trainings">
              <Button variant="ghost" className="text-blue-600 hover:text-blue-700">
                Voir tout
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {trainings.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-4">Aucune formation créée</p>
              <Link to="/trainings/new">
                <Button data-testid="create-first-training-btn" variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Créer ma première formation
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {trainings.slice(0, 5).map((training) => (
                <Link
                  key={training.id}
                  to={`/trainings/${training.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`training-item-${training.id}`}
                >
                  <div className="w-16 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                    {training.thumbnail_url ? (
                      <img 
                        src={training.thumbnail_url} 
                        alt={training.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <BookOpen className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 truncate">{training.title}</h4>
                    <p className="text-sm text-slate-500">
                      {training.module_count} modules • {training.lesson_count} leçons
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    training.status === 'published' 
                      ? 'bg-emerald-50 text-emerald-700'
                      : training.status === 'ready'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {training.status === 'published' ? 'Publié' 
                      : training.status === 'ready' ? 'Prêt'
                      : training.status === 'in_progress' ? 'En cours'
                      : 'Brouillon'}
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Onboarding Dialog */}
      <OnboardingDialog open={showOnboarding} onOpenChange={setShowOnboarding} />
    </div>
  );
};

export default Dashboard;
